/**
 * auditApi.js — single source for the two API calls used across the audit UI:
 *   • getAuditAnswers — per-KPI list of answers + audit chain
 *   • auditAction    — approve/reject one or many stepIds for a KPI
 *
 * AuditList (bulk approve/reject across KPIs), AuditAnswer (per-answer review
 * page), and QualitativeAuditAccordion (single tabular answer) all funnel
 * through these wrappers so the URL + payload shape stays consistent.
 */
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { normalizeAnswers } from "../DataManagment/Qualitative/services/answerShape";

/**
 * GET /v1.0/postLogin/getAuditAnswers
 *
 * @param {object} params
 * @param {number|string} params.questionId
 * @param {number|string} params.financialYearId
 * @param {Array|string}  params.locationIds      array of location ids OR JSON string
 * @param {string}        [params.auditorType]    "INTERNAL" | "EXTERNAL" | undefined
 * @returns {Promise<{ rows: Array, question: object|null, raw: object }>}
 */
export async function fetchAuditAnswers({ questionId, financialYearId, locationIds, auditorType }) {
  const locParam = Array.isArray(locationIds) ? JSON.stringify(locationIds) : locationIds;
  const res = await apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}getAuditAnswers`,
    {},
    { questionId, locationId: locParam, financialYearId, auditorType: auditorType || undefined },
    "GET",
  );
  const rows = res?.data?.data ?? res?.data ?? [];
  const question = res?.data?.question ?? res?.question ?? null;
  return { rows: Array.isArray(rows) ? rows : [], question, raw: res };
}

/**
 * From a list of getAuditAnswers rows, pull stepIds that are still actionable
 * (PENDING or IN_REVIEW). `row.id` is the audit step id.
 */
export const pendingStepIds = (rows) =>
  (rows || [])
    .filter(r => ["PENDING", "IN_REVIEW"].includes((r.auditStatus || "").toUpperCase()))
    .map(r => r.id)
    .filter(Boolean);

/**
 * POST /v1.0/postLogin/auditAction
 *
 * @param {object} payload
 * @param {number|string} payload.questionId
 * @param {Array<number>} payload.stepIds
 * @param {number|string} payload.financialYearId
 * @param {"ACCEPTED"|"REJECTED"} payload.action
 * @param {string} [payload.remark]
 * @param {string} payload.questionType        e.g. "quantitative" | "tabular_question"
 * @param {string} payload.questionTitle
 * @param {number} [payload.answerId]          only for per-answer calls
 */
export async function submitAuditAction({
  questionId,
  stepIds,
  financialYearId,
  action,
  remark = "",
  questionType,
  questionTitle,
  answerId,
  onBehalf,
  targetLevel,
  targetAuditorType,
  finalize,
}) {
  const body = {
    questionId: Number(questionId),
    stepIds,
    financialYearId: Number(financialYearId),
    action,
    remark,
    questionType,
    questionTitle,
  };
  if (answerId !== undefined) body.answerId = answerId;
  if (onBehalf !== undefined) body.onBehalf = onBehalf;
  if (targetLevel !== undefined) body.targetLevel = targetLevel;
  if (targetAuditorType !== undefined) body.targetAuditorType = targetAuditorType;
  if (finalize !== undefined) body.finalize = finalize;
  return apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}auditAction`,
    {},
    body,
    "POST",
  );
}

/**
 * GET /v1.0/postLogin/getFramework — returns the framework id list for the
 * current company. Mirrors the call pattern Qualitative.jsx uses so question-
 * details fetches stay consistent across the data-management + audit modules.
 */
export async function fetchFrameworkIds() {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getFramework`,
      {},
      { type: "ALL" },
      "GET",
      true,
      false,
    );
    if (isSuccess) return (data?.data || []).map((item) => item.id);
  } catch (e) {
    console.error("fetchFrameworkIds error:", e);
  }
  return [];
}

/**
 * GET /v1.0/postLogin/getReportingQuestion (qualitative scope).
 * Returns all qualitative (tabular_question) questions for the given FY,
 * with `details` populated. The audit qualitative tab needs `details` for
 * TabularDynamicForm to dispatch between fixed / dynamic-row / dynamic-column
 * layouts (and ultimately to FullyDynamicForm). Same params Qualitative.jsx
 * uses; if frameworkIds is omitted, it falls back to fetching them.
 *
 * @returns {Promise<Array>} question rows, may be empty
 */
export async function fetchQualitativeQuestions({ financialYearId, frameworkIds }) {
  if (!financialYearId) return [];
  let fwIds = frameworkIds;
  if (!Array.isArray(fwIds) || fwIds.length === 0) {
    fwIds = await fetchFrameworkIds();
    if (fwIds.length === 0) fwIds = [1];
  }
  const { isSuccess, data } = await apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestion`,
    {},
    {
      financialYearId,
      frameworkIds: JSON.stringify(fwIds),
      modulename: "Qualitative",
    },
    "GET",
    true,
    false,
  );
  if (!isSuccess || !data?.data) return [];
  return (data.data || []).filter(q => q.questionType === "tabular_question");
}

/**
 * GET /v1.0/postLogin/qualitativeQuestionAnswer — same endpoint
 * QualitativeRow uses. Carries the tabular grid payload needed for rendering:
 * `answer` (grid JSON), `columnGroups`, `rowNames`, `note`. The audit-step
 * info (stepId / auditLevels / status) still comes from getAuditAnswers; this
 * one is purely the answer-shape source so TabularDynamicForm can render
 * (and self-dispatch to FullyDynamicForm) the same way it does on the
 * Qualitative data-management page.
 */
export async function fetchQualitativeAnswer({ questionId, financialYearId }) {
  if (!questionId || !financialYearId) return null;
  const { isSuccess, data } = await apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}qualitativeQuestionAnswer`,
    {},
    { financialYearId, questionId },
    "GET",
  );
  if (!isSuccess) return null;
  return normalizeAnswers(data?.answers)[0] ?? null;
}

/**
 * Bulk-friendly helper: for a given KPI item, fetch its answers and (a)
 * approve all pending steps, or (b) reject them with a shared remark.
 * Returns the count of stepIds actioned (0 if there was nothing pending).
 *
 * @param {object} args
 * @param {object} args.item              KPI item from getAssignedAuditorKpis
 * @param {number|string} args.fyId
 * @param {string} [args.auditType]
 * @param {"ACCEPTED"|"REJECTED"} args.action
 * @param {string} [args.remark]
 * @param {Array|string} [args.locationIds]  default: item.locations
 */
export async function bulkActionKpi({
  item,
  fyId,
  auditType,
  action,
  remark = "",
  locationIds,
  onBehalf,
  finalize,
}) {
  const locs = locationIds ?? item.locations ?? [];
  const { rows } = await fetchAuditAnswers({
    questionId: item.questionId,
    financialYearId: fyId,
    locationIds: locs,
    auditorType: auditType,
  });
  const stepIds = pendingStepIds(rows);
  if (!stepIds.length) return 0;
  await submitAuditAction({
    questionId: item.questionId,
    stepIds,
    financialYearId: fyId,
    action,
    remark,
    questionType: item.questionType,
    questionTitle: item.title,
    onBehalf,
    finalize,
  });
  return stepIds.length;
}
